import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../global-components/src";
import { PACOCA_API_URL } from "../global-components/src/utils/vars";

interface NotificationBadgeProps {
  isMobile: boolean;
}

// 🔹 Variáveis globais (compartilhadas entre chamadas do hook)
let globalNotificationCount = 0;
let globalNotificationChatCount = 0;
let listeners: (() => void)[] = [];

/** Incrementa o badge de chat em +1 sem precisar ir ao servidor.
 *  Chamado pelo GlobalChatListener quando uma mensagem WS chega fora
 *  da tela de chat para que o badge atualize imediatamente. */
export function incrementGlobalChatBadge(userId: number) {
  globalNotificationChatCount += 1;
  localStorage.setItem(`totalNotificationChat-${userId}`, String(globalNotificationChatCount));
  listeners.forEach((fn) => fn());
}

export const useNotifications = (makeRequisition = true) => {
  const [notificationCount, setNotificationCount] = useState(globalNotificationCount);
  const [notificationChatCount, setNotificationChatCount] = useState(globalNotificationChatCount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { user, token, isAuthenticated } = useAuth();
  const path = location.pathname;
  const isFetching = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchNotifications = async () => {
    if (!user?.id || !isAuthenticated || isFetching.current || error) return;
    isFetching.current = true;

    try {
      setLoading(true);
      setError(false);

      let data = {
        total: Number(localStorage.getItem(`totalNotification-${user.id}`)) || 0,
        chat: Number(localStorage.getItem(`totalNotificationChat-${user.id}`)) || 0,
      };

      if (makeRequisition) {
        const response = await axios.get(`${PACOCA_API_URL}/notifications/count/${user?.id}`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        data = response.data;
      }

      const total = data.total || 0;
      const chat = data.chat || 0;

      // 🔸 Atualiza variáveis globais
      globalNotificationCount = total;
      globalNotificationChatCount = chat;

      // 🔸 Notifica todos os componentes que usam o hook
      listeners.forEach((fn) => fn());

      localStorage.setItem(`totalNotification-${user.id}`, total.toString());
      localStorage.setItem(`totalNotificationChat-${user.id}`, chat.toString());

      setLoading(false);
    } catch (error) {
      setError(true);
      console.error("Erro ao buscar notificações:", error);
    } finally {
      isFetching.current = false;
    }
  };

  // 🔸 Sincroniza este hook com as variáveis globais
  useEffect(() => {
    const listener = () => {
      setNotificationCount(globalNotificationCount);
      setNotificationChatCount(globalNotificationChatCount);
    };
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  useEffect(() => {
    if (path === "/notificacoes") {
      globalNotificationCount = 0;
      setNotificationCount(0);
    }
    if (path === "/chats") {
      globalNotificationChatCount = 0;
      setNotificationChatCount(0);
    }
  }, [path]);

  useEffect(() => {
    const startPolling = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(fetchNotifications, 300000);
      }
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchNotifications();
        startPolling();
      }
    };

    // inicia imediatamente se aba estiver ativa
    if (!document.hidden) {
      fetchNotifications();
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return [notificationCount, notificationChatCount] as const;
};
